import { useState } from 'react';
import { Box, Group, SegmentedControl, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { plants, MONTH_NAMES, LAST_FROST_WEEK, FIRST_FROST_WEEK, type PlantInfo } from '../data/plants';

const TOTAL_WEEKS = 52;
const CELL_W = 16;
const CELL_H = 22;
const LABEL_W = 140;

type Phase = 'indoor' | 'transplant' | 'directSow' | 'harvest';

const PHASE_COLORS: Record<Phase, string> = {
  indoor: '#ae3ec9',
  transplant: '#1c7ed6',
  directSow: '#2f9e44',
  harvest: '#e8590c',
};

const PHASE_LABELS: Record<Phase, string> = {
  indoor: 'Start Indoors',
  transplant: 'Transplant',
  directSow: 'Direct Sow',
  harvest: 'Harvest',
};

function weekToDate(week: number): string {
  const d = new Date(2026, 0, 1);
  d.setDate(d.getDate() + (week - 1) * 7);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getPhaseWeeks(plant: PlantInfo): { phase: Phase; start: number; end: number }[] {
  const phases: { phase: Phase; start: number; end: number }[] = [];
  if (plant.indoorStart) {
    phases.push({ phase: 'indoor', start: plant.indoorStart[0], end: plant.indoorStart[1] });
  }
  if (plant.transplant) {
    phases.push({ phase: 'transplant', start: plant.transplant[0], end: plant.transplant[1] });
  }
  if (plant.directSow) {
    phases.push({ phase: 'directSow', start: plant.directSow[0], end: plant.directSow[1] });
  }
  phases.push({ phase: 'harvest', start: plant.harvestStart, end: plant.harvestEnd });
  return phases;
}

function getEarliestAction(plant: PlantInfo): number {
  let earliest = plant.harvestStart;
  if (plant.indoorStart) earliest = Math.min(earliest, plant.indoorStart[0]);
  if (plant.transplant) earliest = Math.min(earliest, plant.transplant[0]);
  if (plant.directSow) earliest = Math.min(earliest, plant.directSow[0]);
  return earliest;
}

type SortMode = 'name' | 'earliest' | 'harvest' | 'category';

function sortPlants(list: PlantInfo[], mode: SortMode): PlantInfo[] {
  const sorted = [...list];
  switch (mode) {
    case 'name':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case 'earliest':
      return sorted.sort((a, b) => getEarliestAction(a) - getEarliestAction(b));
    case 'harvest':
      return sorted.sort((a, b) => a.harvestStart - b.harvestStart);
    case 'category':
      return sorted.sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
  }
}

function rangesOverlap(a: [number, number], b: [number, number]): boolean {
  return a[0] <= b[1] && b[0] <= a[1];
}

function PlantRow({ plant }: { plant: PlantInfo }) {
  const phases = getPhaseWeeks(plant);
  const hasOverlap = plant.transplant && plant.directSow && rangesOverlap(plant.transplant, plant.directSow);
  const barH = CELL_H - 4;

  return (
    <Group gap={0} wrap="nowrap" style={{ height: CELL_H }}>
      <Text
        size="xs"
        fw={500}
        style={{
          width: LABEL_W,
          minWidth: LABEL_W,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          paddingRight: 8,
        }}
      >
        {plant.name}
      </Text>
      <Box style={{ position: 'relative', width: TOTAL_WEEKS * CELL_W, height: CELL_H }}>
        {phases.map(({ phase, start, end }) => {
          // When transplant and direct sow overlap, stack them top/bottom
          let top = 2;
          let height = barH;
          if (hasOverlap && phase === 'transplant') {
            height = Math.floor(barH / 2);
          } else if (hasOverlap && phase === 'directSow') {
            top = 2 + Math.floor(barH / 2);
            height = barH - Math.floor(barH / 2);
          }

          return (
            <Tooltip
              key={phase}
              label={`${PHASE_LABELS[phase]}: ${weekToDate(start)} – ${weekToDate(end)}`}
              withArrow
            >
              <Box
                style={{
                  position: 'absolute',
                  left: (start - 1) * CELL_W,
                  width: (end - start + 1) * CELL_W,
                  height,
                  top,
                  backgroundColor: PHASE_COLORS[phase],
                  borderRadius: 3,
                  opacity: 0.85,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Group>
  );
}

interface TimelineViewProps {
  filter: string;
  isSelected: (name: string) => boolean;
}

export function TimelineView({ filter, isSelected }: TimelineViewProps) {
  const theme = useMantineTheme();
  const [sortMode, setSortMode] = useState<SortMode>('earliest');

  const filtered = sortPlants(
    plants
      .filter((p) => isSelected(p.name))
      .filter((p) => filter === 'all' || p.category === filter),
    sortMode,
  );

  const monthPositions = MONTH_NAMES.map((name, i) => {
    const weeksPerMonth = [4, 4, 5, 4, 5, 4, 4, 5, 4, 4, 5, 4];
    let startWeek = 1;
    for (let j = 0; j < i; j++) startWeek += weeksPerMonth[j];
    return { name, start: startWeek, weeks: weeksPerMonth[i] };
  });

  return (
    <Box style={{ overflowX: 'auto', paddingBottom: 16 }}>
      {/* Legend + sort */}
      <Group gap="lg" mb="sm" justify="space-between">
        <Group gap="lg">
          {(Object.keys(PHASE_COLORS) as Phase[]).map((phase) => (
            <Group key={phase} gap={4}>
              <Box
                style={{
                  width: 14, height: 14,
                  backgroundColor: PHASE_COLORS[phase],
                  borderRadius: 3,
                }}
              />
              <Text size="xs">{PHASE_LABELS[phase]}</Text>
            </Group>
          ))}
          <Group gap={4}>
            <Box
              style={{
                width: 1, height: 14,
                backgroundColor: theme.colors.red[6],
                borderLeft: `2px dashed ${theme.colors.red[6]}`,
              }}
            />
            <Text size="xs" c="red.6">Frost dates</Text>
          </Group>
        </Group>
        <Group gap="xs">
          <Text size="xs" fw={500}>Sort:</Text>
          <SegmentedControl
            size="xs"
            value={sortMode}
            onChange={(v) => setSortMode(v as SortMode)}
            data={[
              { label: 'Earliest Action', value: 'earliest' },
              { label: 'Harvest', value: 'harvest' },
              { label: 'Name', value: 'name' },
              { label: 'Category', value: 'category' },
            ]}
          />
        </Group>
      </Group>

      {/* Month headers */}
      <Group gap={0} wrap="nowrap" mb={4}>
        <Box style={{ width: LABEL_W, minWidth: LABEL_W }} />
        {monthPositions.map(({ name, weeks }) => (
          <Text
            key={name}
            size="xs"
            fw={600}
            c="dimmed"
            style={{ width: weeks * CELL_W, textAlign: 'center' }}
          >
            {name}
          </Text>
        ))}
      </Group>

      {/* Grid + frost lines */}
      <Box style={{ position: 'relative' }}>
        <Box
          style={{
            position: 'absolute',
            left: LABEL_W + (LAST_FROST_WEEK - 1) * CELL_W,
            top: 0, bottom: 0,
            borderLeft: `2px dashed ${theme.colors.red[6]}`,
            zIndex: 10, pointerEvents: 'none',
          }}
        />
        <Box
          style={{
            position: 'absolute',
            left: LABEL_W + (FIRST_FROST_WEEK - 1) * CELL_W,
            top: 0, bottom: 0,
            borderLeft: `2px dashed ${theme.colors.red[6]}`,
            zIndex: 10, pointerEvents: 'none',
          }}
        />

        {filtered.map((plant) => (
          <PlantRow key={plant.name} plant={plant} />
        ))}
      </Box>
    </Box>
  );
}
